'use client';

import config from '@packages/config';
import type { Locale } from '@packages/config/src/default';
import { Link } from '@packages/modules/src/Link/Link';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useState, useSyncExternalStore } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageValue, NoMessageValues } from 'react-intl';
import { subscribeToNothing } from '@packages/shared/src/utils/subscribeToNothing/subscribeToNothing';

const Messages = defineMessages<{
  readonly link: NoMessageValues;
  readonly headline: { readonly providerName: MessageValue };
  readonly description: {
    readonly link: MessageValue;
    readonly provider: MessageValue;
    readonly providerName: MessageValue;
  };
  readonly button: NoMessageValues;
}>({
  link: {
    id: 'video.overlay.description.link.label',
    defaultMessage: 'Datenschutzerklärung',
  },
  headline: {
    id: 'video.overlay.headline',
    defaultMessage: '{providerName}-Inhalte aktivieren',
  },
  description: {
    id: 'video.overlay.description',
    defaultMessage: `Um dein Einkaufserlebnis so abwechslungsreich wie möglich zu gestalten, 
      zeigen wir in unserem Onlineshop auch {providerName}-Videos. Damit du sie 
      ansehen kannst, brauchen wir deine Einwilligung. Denn deine personenbezogenen 
      Daten werden dabei zur Analyse an {provider} weitergegeben. 
      Alle Infos findest du in unserer {link}.`,
  },
  button: {
    id: 'video.overlay.button.label',
    defaultMessage: 'INHALTE AKTIVIEREN',
  },
});

const Overlay = ({
  onClickActivateContent,
  texts: { provider, providerName },
}: {
  onClickActivateContent: Function;
  texts: { provider?: string; providerName: string };
}) => {
  const intl = useIntl();
  return (
    <div className="bg-color-grey-light absolute top-0 left-0 flex size-full min-h-[10rem] min-w-[20rem] flex-col justify-between overflow-auto md:min-h-[15rem] md:min-w-full">
      <div className="text-color-primary-contrasttext bg-color-primary px-4 py-1 font-bold">
        {intl.formatMessage(Messages.headline, { providerName })}
      </div>
      <div className="mb-2 overflow-auto px-4 py-2">
        {intl.formatMessage(Messages.description, {
          providerName,
          // it is optional to use another provider label in text content
          provider: provider ?? providerName,
          link: (
            <Link
              href={config.seoContent.url[intl.locale as Locale].privacyNoticePath}
              title={intl.formatMessage(Messages.link)}
              className="text-color-text-dark underline"
            >
              {intl.formatMessage(Messages.link)}
            </Link>
          ),
        })}
      </div>
      <div className="mb-1 grow self-center px-4">
        <Button onClick={() => onClickActivateContent()} color="primary">
          {intl.formatMessage(Messages.button)}
        </Button>
      </div>
    </div>
  );
};

export const VideoConsentOverlay = ({
  children,
  sessionStorageKey,
  texts,
}: {
  children: React.ReactNode;
  sessionStorageKey: string;
  texts: {
    provider?: string;
    providerName: string;
  };
}) => {
  const isAllowed: boolean = useCookieConsent('C0004');
  // customize storage key to prevent duplicated keys
  const overlayStorageKey = `videoConsentOverlay-${sessionStorageKey}`;
  const activatedInSession = useSyncExternalStore(
    subscribeToNothing,
    () => sessionStorage.getItem(overlayStorageKey) === '1',
    () => false,
  );
  const [overlayVisible, setOverlayVisible] = useState(true);

  if (overlayVisible && (activatedInSession || isAllowed)) {
    setOverlayVisible(false);
  }

  const onClickActivateContent = () => {
    sessionStorage.setItem(overlayStorageKey, '1');
    setOverlayVisible(false);
  };

  return overlayVisible ? (
    <Overlay onClickActivateContent={onClickActivateContent} texts={texts} />
  ) : (
    children
  );
};
